import { useStaticQuery, graphql } from 'gatsby';

import type { Lucid } from '../types/types';

type CompetitorLink = {
  label: string;
  value: string;
};

type UseCompetitor = {
  directus: {
    competitor2Pages: Lucid.Directus.Generated.Competitor_2[];
    competitorPages: Lucid.Directus.Generated.Competitor[];
  };
  i18nData: Lucid.I18n.DataEdges;
};

const useCompetitor = (languageKey: Lucid.I18n.LanguagesShortCode) => {
  const {
    directus: { competitor2Pages, competitorPages },
    i18nData,
  } = useStaticQuery<UseCompetitor>(graphql`
    query UseCompetitorQuery {
      directus {
        competitor2Pages: competitor_2(limit: 1000, filter: { status: { _eq: "published" } }, sort: "competitor") {
          competitor
          translations(filter: { is_published: { _eq: true } }) {
            languages_code {
              short_code
            }
            path {
              url
            }
          }
        }
        competitorPages: competitor(limit: 1000, filter: { status: { _eq: "published" } }, sort: "competitor") {
          competitor
          translations(filter: { is_published: { _eq: true } }) {
            languages_code {
              short_code
            }
            path {
              url
            }
          }
        }
      }
      i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
        edges {
          node {
            name
            childSrcJson {
              mktg_competitor_compare
            }
          }
        }
      }
    }
  `);

  const allCompetitorPages = [...competitor2Pages, ...competitorPages];

  const competitors = allCompetitorPages
    .reduce((uniqueCompetitors, page) => {
      const { competitor, translations } = page;
      const label = competitor ?? '';
      const translatedPage = translations?.find(t => t.languages_code?.short_code === languageKey);

      if (!translatedPage || !translatedPage.path) {
        return uniqueCompetitors;
      }

      if (translatedPage && !uniqueCompetitors.find(c => c.label === label)) {
        return [...uniqueCompetitors, { label, value: translatedPage?.path?.url || '' }];
      }

      return uniqueCompetitors;
    }, [] as CompetitorLink[])
    .sort((a, b) => a.label.localeCompare(b.label));

  return {
    competitorPages: competitors,
    competitorI18nData: i18nData,
  };
};

export default useCompetitor;
